/** Priority 1 is highest (Todoist convention); 4 is the default "none". */
export type Priority = 1 | 2 | 3 | 4;

export type Kind = 'todo' | 'note';

/** Fields every synced row carries. `dirty` is local-only — it never leaves the device. */
interface Syncable {
  /** Client-generated UUID. Offline creates must work, so IDs cannot come from the server. */
  id: string;
  /** Set by the Postgres trigger in the real backend; locally we stamp it on write. */
  updated_at: string;
  created_at: string;
  /** Tombstone. Nothing is ever hard-deleted — that is how the other device learns it is gone. */
  deleted: boolean;
  /** 1 = needs pushing. Stored as a number because Dexie cannot index booleans. */
  dirty: 0 | 1;
}

/**
 * The default list. Every item belongs to exactly one list, so there has to be
 * somewhere for an uncategorised capture to land.
 *
 * The id is a fixed constant rather than generated: two devices creating their
 * own Inbox offline would otherwise sync into two of them, with no way to tell
 * which was which. A well-known id makes the create idempotent everywhere.
 */
export const INBOX_LIST_ID = '11111111-1111-4111-8111-111111111111';

/**
 * Where a note lives. Answers: *where does this note belong?*
 *
 * Exactly one per item, always.
 */
export interface List extends Syncable {
  name: string;
  /** Float, so reordering means averaging neighbours — no renumbering pass. */
  sort_order: number;
  /** A key from the palette, or null for the default neutral marker. */
  color: string | null;
  /** The Inbox cannot be deleted or moved; everything else can. */
  system: boolean;
}

/**
 * A tag that exists only inside one list. Answers: *what is this note about
 * within this list?*
 *
 * Deleting the parent list takes its list tags with it — they have no meaning
 * anywhere else, which is the whole point of them.
 */
export interface ListTag extends Syncable {
  list_id: string;
  name: string;
  color: string | null;
  sort_order: number;
}

/**
 * A tag that cuts across every list. Answers: *how should this note be
 * categorised across the whole application?*
 */
export interface GlobalTag extends Syncable {
  name: string;
  color: string | null;
  sort_order: number;
}

export interface Item extends Syncable {
  kind: Kind;
  title: string;
  /** Markdown. Kept a plain string so last-write-wins stays trivial and export is free. */
  body: string;
  done: boolean;
  completed_at: string | null;
  /** The actual deadline. */
  due_at: string | null;
  /** When I intend to work on it — hides future work from Today without losing the deadline. */
  start_at: string | null;
  /** Independent of due_at. */
  remind_at: string | null;
  priority: Priority;
  /** Never null. An item with no home belongs to the Inbox. */
  list_id: string;
  /** Only ever ids of tags belonging to `list_id`. Moving list clears them. */
  list_tag_ids: string[];
  /** Independent of the list, and survives a move. */
  global_tag_ids: string[];
  /** Float, so inserting between two rows is an average — no renumbering pass. */
  sort_order: number;
  /** Reserved: the hook for later extracting inline `- [ ]` tasks out of note bodies. */
  source_note_id: string | null;
  /**
   * When this item entered the Trash, or null while it is live.
   *
   * Its own column rather than a reading of `updated_at`, because `updated_at`
   * moves for reasons that have nothing to do with deletion — a sync push, a
   * parent backfill — and every one of those would silently restart the
   * retention clock on something already in the bin.
   */
  deleted_at: string | null;
}

export interface Attachment extends Syncable {
  item_id: string;
  storage_path: string;
  mime_type: string;
  width: number | null;
  height: number | null;
  bytes: number;
  sort_order: number;
  /** 0 until the blob has reached Storage. */
  uploaded: 0 | 1;
}

/** Cached image binary, keyed by attachment. Immutable and content-addressed. */
export interface Blob_ {
  attachment_id: string;
  data: Blob;
}

export interface Meta {
  key: string;
  value: unknown;
}

/** LWW resolves silently; every conflict is recorded so I can tell whether it ever matters. */
export interface Conflict {
  id?: number;
  item_id: string;
  at: string;
  title: string;
  local_updated_at: string;
  remote_updated_at: string;
  winner: 'local' | 'remote';
  losing_body: string;
}

/** What the sidebar has highlighted. Exactly one thing at a time. */
export type Selection =
  | { type: 'today' }
  | { type: 'upcoming' }
  /**
   * The month being looked at, as `YYYY-MM`.
   *
   * It rides in the Selection rather than in component state because `select()`
   * rebuilds the view on every navigation — parked in a `useState` the month
   * would silently snap back to today every time you left and returned.
   */
  | { type: 'calendar'; month: string }
  | { type: 'inbox' }
  | { type: 'all' }
  | { type: 'trash' }
  | { type: 'conflicts' }
  | { type: 'settings' }
  | { type: 'list'; listId: string }
  | { type: 'listTag'; listId: string; tagId: string }
  | { type: 'globalTag'; tagId: string };

/**
 * The selection, plus any filters stacked on top of it.
 *
 * Keeping them separate is what makes "List + Global Tag" work without the
 * sidebar having to represent a compound state: the sidebar shows where you
 * are, the chips in the list header show what you have narrowed it to.
 */
export interface View {
  selection: Selection;
  /** Additional global tags narrowing the current selection. AND, not OR. */
  globalTagIds: string[];
  /** Free-text search, applied on top of everything else. */
  query: string;
}

/**
 * How long a deleted item stays recoverable. After this it is purged for real —
 * locally, on the server, and out of Storage.
 */
export const TRASH_RETENTION_DAYS = 30;

export const emptyView = (selection: Selection): View => ({
  selection,
  globalTagIds: [],
  query: '',
});
